/* usePush — Web Push subscription for THIS browser/device (docs/15 C).
 *
 * The device-level half of push: register the service worker, ask permission,
 * subscribe with the server's VAPID key, and hand the subscription to the backend
 * (/push/subscribe). The per-channel opt-ins (CoachPushEnabled, RealtimePush) are
 * separate settings — this is only "can this device be reached at all".
 *
 * Platform reality baked in:
 *  - Android / desktop Chrome/Edge: works in a normal tab.
 *  - iOS / Safari: push ONLY works once the site is INSTALLED to the home screen
 *    (16.4+). We detect iOS-not-installed and expose it so the UI can coach the
 *    Share -> Add to Home Screen gesture instead of firing a permission prompt
 *    that would do nothing.
 */
import { ref } from 'vue'
import axios from 'axios'
import { getAPIURL } from '@/config'

const API_URL = getAPIURL()

// module-level so every caller shares one view of the device's state
const permission = ref(typeof Notification !== 'undefined' ? Notification.permission : 'default')
const subscribed = ref(false)
const busy = ref(false)

// --- PWA install (Chrome: Android + desktop) -------------------------------
// beforeinstallprompt fires ONCE, early in page load. These listeners are at
// module scope and main.js imports this file for that side effect, so they are
// attached before the event can fire — do not move them inside usePush(), and do
// not lazy-load this module, or the install button silently never appears.
//
// We preventDefault() Chrome's own mini-infobar: it flashes past and auto-dismisses
// (Benny missed it on his own product), which is useless for getting a store manager
// to install. We stash the event and fire it from a real button instead.
const deferredInstallPrompt = ref(null)
const canInstall = ref(false)
const installed = ref(false)

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredInstallPrompt.value = e
    canInstall.value = true
  })
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt.value = null
    canInstall.value = false
    installed.value = true
  })
}

/** Fire Chrome's real install dialog. Returns 'accepted' | 'dismissed' |
 *  'unavailable' (no captured event — e.g. iOS, or already installed). */
async function promptInstall() {
  const e = deferredInstallPrompt.value
  if (!e) return 'unavailable'
  e.prompt()
  const { outcome } = await e.userChoice
  // the event is single-use — Chrome will hand us a fresh one on a later load
  deferredInstallPrompt.value = null
  canInstall.value = false
  return outcome
}

function isSupported() {
  return typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    typeof Notification !== 'undefined'
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+ reports as Mac; disambiguate by touch
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function isStandalone() {
  return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true
}

// iOS in a plain Safari tab can't do push — it must be installed first.
function needsIOSInstall() {
  return isIOS() && !isStandalone()
}

function _authHeader() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` } }
}

// VAPID public key (base64url) -> Uint8Array for pushManager.subscribe.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

// Reflect whether this device already holds a live subscription.
async function refresh() {
  if (!isSupported()) return
  permission.value = Notification.permission
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    subscribed.value = !!sub
  } catch (_) {
    subscribed.value = false
  }
}

// Ask permission (only meaningful after a user gesture), subscribe, register with
// the backend. Returns true on success. Throws are surfaced to the caller to toast.
async function enable() {
  if (!isSupported()) throw new Error('This browser does not support notifications')
  if (needsIOSInstall()) throw new Error('Add Storalytic to your Home Screen first')
  busy.value = true
  try {
    const reg = await navigator.serviceWorker.ready

    const perm = await Notification.requestPermission()
    permission.value = perm
    if (perm !== 'granted') throw new Error('Notifications were not allowed')

    const { data } = await axios.get(`${API_URL}/push/vapid-public-key`, _authHeader())
    let sub = await reg.pushManager.getSubscription()
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey)
      })
    }
    const json = sub.toJSON()
    await axios.post(`${API_URL}/push/subscribe`, {
      endpoint: json.endpoint,
      keys: json.keys
    }, _authHeader())

    subscribed.value = true
    return true
  } finally {
    busy.value = false
  }
}

async function disable() {
  busy.value = true
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    if (sub) {
      const endpoint = sub.endpoint
      await sub.unsubscribe()
      try {
        await axios.post(`${API_URL}/push/unsubscribe`, { endpoint }, _authHeader())
      } catch (_) { /* backend cleanup is best-effort */ }
    }
    subscribed.value = false
  } finally {
    busy.value = false
  }
}

export function usePush() {
  return {
    permission, subscribed, busy,
    isSupported, isIOS, isStandalone, needsIOSInstall,
    refresh, enable, disable,
    // PWA install
    canInstall, installed, promptInstall
  }
}
